import { type FormEvent, useState } from 'react';
import { Mail } from 'lucide-react';
import { Button, Input } from '@wundo/ui';
import { ApiError } from '@wundo/client/api.ts';
import { signInWithCode, verifySignInToken } from '@wundo/client/auth.ts';
import { leaveTo } from '@wundo/client/leaveTo.ts';
import { m } from '@/paraglide/messages.js';
import { refusedSignInMessage } from './refusedSignInMessage.ts';

interface SentConfirmationProps {
  email: string;
  onReset: () => void;
}

/*
 * The mail's link signs its reader in with one press, so this screen mostly waits.
 * The code form is for the one case the link cannot serve: the mail is open
 * on a different device than the one sitting at this screen.
 */
export function SentConfirmation({ email, onReset }: SentConfirmationProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!code.trim()) {
      return;
    }

    setError('');
    setLoading(true);
    try {
      const token = await signInWithCode(email, code);
      /*
       * The same door the link's button posts to, and the same token: where it
       * lands was settled when the mail was sent. The browser is on its way
       * out.
       */
      const { destination } = await verifySignInToken(token);
      leaveTo(destination);
    } catch (caught) {
      setError(typedCodeErrorMessage(caught));
      setLoading(false);
    }
  }

  return (
    <div className='flex animate-pop flex-col items-center gap-4.5 text-center'>
      <div className='flex size-16.5 items-center justify-center rounded-xl bg-type-func-tint text-type-func'>
        <Mail className='size-7.5' aria-hidden='true' />
      </div>
      <h1 className='text-xl font-semibold tracking-tight'>{m.login_sent_title()}</h1>
      <p className='max-w-[32ch] text-md text-ink-muted'>
        {m.login_sent_body({ email })}
      </p>
      <form
        className='flex w-full flex-col gap-2.75'
        onSubmit={(event) => void handleSubmit(event)}
        noValidate
      >
        <div>
          <label
            className='mb-1.75 block text-sm font-semibold text-ink-secondary'
            htmlFor='code'
          >
            {m.login_code_label()}
          </label>
          <Input
            id='code'
            type='text'
            fieldSize='lg'
            autoComplete='one-time-code'
            autoCapitalize='characters'
            spellCheck={false}
            maxLength={6}
            value={code}
            invalid={Boolean(error)}
            onChange={(event) => {
              setCode(event.target.value);
              if (error) {
                setError('');
              }
            }}
          />
        </div>
        {error && <span className='text-sm font-medium text-danger'>{error}</span>}
        <Button
          type='submit'
          variant='accent'
          shape='block'
          size='lg'
          fullWidth
          loading={loading}
        >
          {m.login_code_submit()}
        </Button>
      </form>
      <Button type='button' variant='ghost' size='sm' onClick={onReset}>
        {m.login_sent_other_email()}
      </Button>
    </div>
  );
}

/*
 * The code names the token the link carries, and that token still goes through
 * the door, so this form meets the door's refusals too. A dead or foreign token
 * is not a mistyped code: "that code is wrong" would send someone hunting a typo.
 */
function typedCodeErrorMessage(caught: unknown): string {
  if (!(caught instanceof ApiError)) {
    return m.login_code_invalid();
  }
  if (caught.code === 'INVALID_TOKEN' || caught.code === 'LINK_OTHER_DEVICE') {
    return refusedSignInMessage(caught.code);
  }
  if (caught.type === 'rate_limited') {
    return m.login_error_rate_limited();
  }
  return m.login_code_invalid();
}
